/**
 * AICoachParticipantList Component
 *
 * Displays the list of participants in the AI Coach session, with Aria pinned
 * on top as the team's AI coach.
 *
 * Responsive:
 * - Mobile: Smaller avatars, compact layout
 * - Desktop: Full-size avatars and padding
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { AriaBrand } from '../../../../../shared/components/AriaBrand'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { AI_COACH_COLORS, AI_COACH_STYLES } from '../constants'

interface AICoachParticipantListProps {
  participants: string[]
  participantNames: Record<string, string>
  currentUserId: string | undefined
}

/**
 * Get initials from a name (first letter uppercase)
 */
const getInitial = (name: string): string => {
  return name[0]?.toUpperCase() || '?'
}

const participantRowSx = {
  display: 'flex',
  alignItems: 'center',
  padding: { xs: '10px 12px', sm: '12px 14px', md: '12px 16px', lg: '12px 16px' },
  backgroundColor: AI_COACH_COLORS.cardBackground,
  borderRadius: AI_COACH_STYLES.borderRadius.medium,
  border: `1px solid ${INTERAX_COLORS.white10}`,
  gap: { xs: 1, sm: 1.5 }
}

const avatarSx = {
  width: { xs: AI_COACH_STYLES.avatarSize.sm, sm: AI_COACH_STYLES.avatarSize.md },
  height: { xs: AI_COACH_STYLES.avatarSize.sm, sm: AI_COACH_STYLES.avatarSize.md },
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: { xs: 12, sm: 14 },
  fontWeight: 600,
  color: '#fff',
  flexShrink: 0
}

export const AICoachParticipantList: React.FC<AICoachParticipantListProps> = ({
  participants,
  participantNames,
  currentUserId
}) => {
  const { t } = useTranslation()

  return (
    <>
      <Typography
        sx={{
          color: INTERAX_COLORS.white,
          fontSize: { xs: 13, sm: 14, md: 14, lg: 14 },
          fontWeight: 600,
          mb: { xs: 1.5, sm: 2 }
        }}
      >
        {t('aiCoach.ui.teamReadiness')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1, sm: 1.5 },
          flex: 1
        }}
      >
        {/* Aria — the AI coach is always part of the team */}
        <Box sx={{ ...participantRowSx, border: `1px solid ${INTERAX_COLORS.sun40}` }}>
          <Box sx={{ ...avatarSx, backgroundColor: INTERAX_COLORS.sun, color: INTERAX_COLORS.offBlack }}>
            <SparklesIcon style={{ width: 16, height: 16 }} />
          </Box>
          <Typography
            sx={{
              color: INTERAX_COLORS.white,
              fontSize: { xs: 12, sm: 14 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            <AriaBrand />
          </Typography>
        </Box>

        {participants.map((participantId) => {
          const name = participantNames[participantId] || participantId.slice(0, 8)
          const isCurrentUser = participantId === currentUserId

          return (
            <Box key={participantId} sx={participantRowSx}>
              {/* Avatar */}
              <Box sx={{ ...avatarSx, backgroundColor: INTERAX_COLORS.thunderGrey }}>{getInitial(name)}</Box>

              {/* Name */}
              <Typography
                sx={{
                  color: INTERAX_COLORS.white,
                  fontSize: { xs: 12, sm: 14 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {name}
                {isCurrentUser && ` ${t('aiCoach.ui.youIndicator')}`}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </>
  )
}
